import { FC, KeyboardEvent, useRef, useState } from 'react'
import styles from '../companyProfile.module.scss'
import { MAX_ENTRY_LENGTH } from '../types'

interface ChipInputProps {
  emptyPlaceholder: string
  // By name rather than by resulting list, for the same batching reason as the
  // priority grid.
  onAdd: (name: string) => void
  onRemove: (name: string) => void
  value: string[]
}

// Enter commits whatever has been typed as a chip. Backspace on an empty box
// removes the last chip, which is what every tag input in the product does.
// A comma commits too: these fields read as comma-separated lists, and their
// placeholders taught people to type them that way.
export const ChipInput: FC<ChipInputProps> = ({
  emptyPlaceholder,
  onAdd,
  onRemove,
  value,
}) => {
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Split on commas rather than taking the box verbatim. The placeholders
  // advertise comma-separated lists, so people paste one — and a paste fires
  // no keydown, so the comma shortcut alone would leave the whole line as a
  // single chip. An entry that genuinely contains a comma is the price, and
  // the backend splits a comma string the same way.
  const commit = () => {
    const names = draft
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean)
    if (!names.length) return
    names.forEach(onAdd)
    setDraft('')
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      commit()
      return
    }
    if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      onRemove(value[value.length - 1])
    }
  }

  return (
    <div
      className={styles.chipInput}
      onClick={() => inputRef.current?.focus()}
      role="presentation"
    >
      {value.map((name) => (
        <span className={styles.chip} key={name}>
          {name}
          <button
            aria-label={`Remove ${name}`}
            className={styles.chipRemove}
            onClick={(e) => {
              e.stopPropagation()
              onRemove(name)
            }}
            type="button"
          >
            ×
          </button>
        </span>
      ))}
      <input
        className={styles.chipInputControl}
        maxLength={MAX_ENTRY_LENGTH}
        onBlur={commit}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={value.length ? 'Add another' : emptyPlaceholder}
        ref={inputRef}
        value={draft}
      />
    </div>
  )
}
